import React, { FC, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { getAliceAgentApp, getAliceAgentAppUserAgreements } from 'client/api/services/aliceAgentApps';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import { sortUserAgreements, UserAgreement } from 'client/model/userAgreement';
import { buildCorrectedAppPath, getAliceAppType } from 'client/utils/aliceAppRoute';

import { AliceAgentAppSharingPage } from '../AliceAgentAppSharingPage/AliceAgentAppSharingPage';

import { AliceAgentAppEditPage } from './AliceAgentAppEditPage';
import { AliceAgentAppLayout } from './AliceAgentAppLayout';

interface Props {
    skillId: string;
}

export const AliceAgentAppPageContainer: FC<Props> = ({ skillId }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [app, setApp] = useState<AliceAgentAppDTO>();
    const [userAgreements, setUserAgreements] = useState<UserAgreement[]>([]);
    const location = useLocation();

    const isDraftView = !location.pathname.includes('/published/');

    const fetchApp = useCallback(async () => {
        try {
            const [nextApp, agreements] = await Promise.all([
                getAliceAgentApp(skillId),
                getAliceAgentAppUserAgreements(skillId, isDraftView),
            ]);
            setApp(nextApp);
            setUserAgreements(sortUserAgreements(agreements));
        } catch (err) {
            console.error('@Error in fetch getAliceAgentApp', err);
        } finally {
            setIsLoading(false);
        }
    }, [skillId, isDraftView]);

    useEffect(() => {
        fetchApp();
    }, [fetchApp]);

    if (isLoading && !app) return <PageLoader />;
    if (!app) return <Navigate to={routes.app.aliceApps} replace />;

    const correctedPath = buildCorrectedAppPath(location.pathname, getAliceAppType(app));
    if (correctedPath && correctedPath !== location.pathname) {
        return <Navigate to={correctedPath} replace />;
    }

    return (
        <AliceAgentAppLayout app={app} isDraft={isDraftView}>
            <Routes>
                <Route
                    path="draft/settings"
                    element={
                        <AliceAgentAppEditPage
                            app={app}
                            isDraft
                            fetchedUserAgreements={userAgreements}
                            onSaved={fetchApp}
                        />
                    }
                />
                <Route
                    path="published/settings"
                    element={
                        app.published ? (
                            <AliceAgentAppEditPage
                                app={app}
                                isDraft={false}
                                fetchedUserAgreements={userAgreements}
                                onSaved={fetchApp}
                            />
                        ) : (
                            <Navigate to="../draft/settings" replace />
                        )
                    }
                />
                <Route path="sharing" element={<AliceAgentAppSharingPage app={app} />} />
                <Route path="*" element={<Navigate to="draft/settings" replace />} />
            </Routes>
        </AliceAgentAppLayout>
    );
};
